import { Typography, Button, Collapse } from '@mui/material';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { getRelativeDateFromNow } from '../../../utils/date.util';
import messages from '../messages';
import NotificationIgnoreButton from './NotificationIgnoreButton';
import { getDuplicateEmailMembersAction } from '../../../managers/NotificationListManager/actions';
import { LOADING_STATES } from '../../../constants/loading-state.constant';
import DuplicateMemberListSkeleton from '../../common/skeletons/DuplicateMemberListSkeleton';
import DuplicateMemberList from './DuplicateMemberList';
import { FlexTextContainer } from '../../common/ui/Container';

function DuplicateEmailNotification({
  createdAt,
  updatedAt,
  status,
  disableIgnore,
  onIgnore,
  canIgnore,
  notification,
  view,
}) {
  const intl = useIntl();
  const translatedMessages = useMemo(
    () => ({
      viewTxt: intl.formatMessage(messages.viewMembers),
      hideTxt: intl.formatMessage(messages.hideMembers),
    }),
    [intl],
  );
  const relativeDateInSecond = getRelativeDateFromNow(updatedAt || createdAt);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const {
    entityId: email,
    duplicateMembers,
    duplicateMemberLoadingState,
  } = notification;

  const fetchDuplicateMembers = useCallback(
    event => {
      event.stopPropagation();
      if (
        duplicateMemberLoadingState !== LOADING_STATES.SUCCESS &&
        duplicateMemberLoadingState !== LOADING_STATES.LOADING
      ) {
        dispatch(
          getDuplicateEmailMembersAction(
            LOADING_STATES.LOADING,
            email,
            notification.id,
          ),
        );
      }
      setExpanded(prev => !prev);
    },
    [email, dispatch, notification.id, duplicateMemberLoadingState],
  );

  return (
    <FlexTextContainer
      sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
    >
      <div>
        <Typography variant="subtitle">
          <FormattedMessage
            id={messages.duplicateEmail.id}
            defaultMessage={messages.duplicateEmail.defaultMessage}
            values={{
              // eslint-disable-next-line react/no-unstable-nested-components
              emailEle: chunks => (
                <Typography component="span" fontWeight="bold">
                  {chunks}
                </Typography>
              ),
              email,
            }}
          />
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={fetchDuplicateMembers}
          disabled={duplicateMemberLoadingState === LOADING_STATES.LOADING}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          sx={{ ml: 1 }}
        >
          {expanded ? translatedMessages.hideTxt : translatedMessages.viewTxt}
        </Button>
      </div>
      <Typography variant="caption">{relativeDateInSecond}</Typography>
      {status === 'active' && canIgnore && (
        <NotificationIgnoreButton
          onClickHandler={onIgnore}
          loading={disableIgnore}
        ></NotificationIgnoreButton>
      )}
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        sx={{ width: '100%' }}
      >
        {duplicateMemberLoadingState === LOADING_STATES.LOADING ? (
          <DuplicateMemberListSkeleton />
        ) : null}
        {duplicateMemberLoadingState === LOADING_STATES.SUCCESS ? (
          <DuplicateMemberList members={duplicateMembers} view={view} />
        ) : null}
      </Collapse>
    </FlexTextContainer>
  );
}
export default memo(DuplicateEmailNotification);
